import { useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "../components/Logo";

export default function LoginPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const u = await login(username.trim(), password);
      const next = search.get("next") || (u.must_change_password ? "/change-password" : "/livetv");
      navigate(next, { replace: true });
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } }; message?: string }).response?.data
          ?.detail || (e as { message?: string }).message || "Login failed";
      setErr(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-canvas overflow-hidden">
      <div className="absolute inset-0 z-0">
        <video autoPlay muted loop playsInline className="w-full h-full object-cover animate-ken-burns">
          <source src="/assets/videos/login-bg-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 gradient-overlay-full" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "url(/assets/images/login-grain.jpg)",
            backgroundRepeat: "repeat",
            backgroundSize: "256px 256px",
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mb-3 flex items-center"
        >
          <Logo className="h-20 w-auto" />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-[18px] font-ui text-text-secondary mb-10 text-center"
        >
          Your personal broadcast.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-[420px] glass-panel rounded-xl p-8"
        >
          <h2 className="font-display text-[26px] font-bold text-text-primary mb-6 tracking-[-0.02em] leading-none">
            Sign In
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary mb-1.5">
                Username
              </label>
              <input
                type="text"
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full h-11 bg-surface border border-muted rounded-lg px-4 font-ui text-[15px] text-text-primary placeholder:text-text-muted/60 gold-focus transition-fast"
              />
            </div>
            <div>
              <label className="block text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 bg-surface border border-muted rounded-lg px-4 pr-11 font-ui text-[15px] text-text-primary placeholder:text-text-muted/60 gold-focus transition-fast"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-fast"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err && <p className="text-[12px] font-ui text-danger -mt-1">{err}</p>}

            <button
              type="submit"
              disabled={busy || !username.trim() || !password}
              className="w-full h-12 bg-accent-gold text-canvas rounded-xl text-[13px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
            <p className="text-[11px] font-ui text-text-secondary text-center pt-2">
              Accounts are managed by your administrator.
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
